import { formatLocalityName } from "@/utils/formatLocality";

export type ShipmentItem = {
  id: string;
  name: string;
  quantity: number;
  weightVolume: string;
  weightValue?: number;
  weightUnit?: "kg" | "ml";
  unitValueBRL: number;
};

export type PackageItem = {
  id: string;
  name?: string;
  widthCm: number;
  heightCm: number;
  lengthCm: number;
  selectedPresetId?: string;
  items: ShipmentItem[];
};

export type PackagePreset = {
  id: number;
  name: string;
  width: string;
  height: string;
  length: string;
  weight: string;
  unit_value: string;
};

export type Location = {
  id: number;
  name: string;
  state?: string;
  slug?: string;
  ibge_id?: number;
  address?: string;
  number?: string;
  city_name?: string;
  state_uf?: string;
};

export type DropoffPoint = {
  id: number;
  name: string;
  address?: string;
  number?: string;
  complement?: string | null;
  district?: string;
  cep?: string;
  phone?: string;
  available_hours?: string | null;
  ibge_id?: number;
  city_name?: string;
  state_uf?: string;
};

// Local state shapes for the pacotes (packages) page.
export type StoredLocation = {
  id?: number;
  name?: string;
  city_name?: string;
  state_uf?: string;
  state?: string;
};

export type StoredPackage = {
  id: string;
  name?: string;
  widthCm: number;
  heightCm: number;
  lengthCm: number;
  weightKg: number;
  unitValueBRL: number;
  quantity: number;
  items?: ShipmentItem[];
};

export type ItemState = {
  items: ShipmentItem[];
  inputValues: Record<string, string>;
};

export function locationLabel(loc: StoredLocation): string {
  return formatLocalityName(loc.name, loc.city_name);
}

export function locationState(loc: StoredLocation): string {
  return loc.state_uf ?? loc.state ?? "";
}

export function dropoffPointToLocation(dp: DropoffPoint): Location {
  return {
    id: dp.id,
    name: dp.name,
    ibge_id: dp.ibge_id,
    address: dp.address,
    number: dp.number,
    city_name: dp.city_name,
    state_uf: dp.state_uf,
  };
}

export function createNewShipmentItem(): ShipmentItem {
  return {
    id: `${Date.now()}_${Math.round(Math.random() * 1e6)}`,
    name: "",
    quantity: 1,
    weightVolume: "",
    weightValue: undefined,
    weightUnit: "kg",
    unitValueBRL: 0,
  };
}

function randDim(max: number) {
  return Math.round((1 + Math.random() * (max - 1)) * 10) / 10;
}

// Inteiro de propósito: peso fracionado faz o quick-quote responder 502, e
// pacotes gerados aqui não devem cair nessa correção depois.
function randWeight() {
  return Math.round(1 + Math.random() * 29);
}

function randValue() {
  return Math.round(10 + Math.random() * 990);
}

export function createNewPackage(): PackageItem {
  return {
    id: `${Date.now()}_${Math.round(Math.random() * 1e6)}`,
    lengthCm: randDim(39),
    widthCm: randDim(28.5),
    heightCm: randDim(18),
    selectedPresetId: "",
    items: [
      {
        ...createNewShipmentItem(),
        weightValue: randWeight(),
        weightUnit: "kg",
        unitValueBRL: randValue(),
      },
    ],
  };
}

export function createInitialPackage(): PackageItem {
  return {
    id: "initial",
    lengthCm: 17,
    widthCm: 12,
    heightCm: 2,
    selectedPresetId: "",
    items: [
      {
        ...createNewShipmentItem(),
        weightValue: 0.3,
        weightUnit: "kg",
        unitValueBRL: 150,
      },
    ],
  };
}

export function parseWeightFromVolume(weightVolume: string): number {
  const kgMatch = weightVolume.match(/(\d+(?:[.,]\d+)?)\s*kg/i);
  if (kgMatch) return parseFloat(kgMatch[1].replace(",", "."));
  const numMatch = weightVolume.match(/(\d+(?:[.,]\d+)?)/);
  return numMatch ? parseFloat(numMatch[1].replace(",", ".")) : 0;
}

export function itemWeightToKg(item: ShipmentItem): number {
  if (item.weightValue != null && item.weightUnit) {
    const qty = item.quantity || 1;
    return item.weightUnit === "ml"
      ? item.weightValue * 0.001 * qty
      : item.weightValue * qty;
  }
  return parseWeightFromVolume(item.weightVolume || "") * (item.quantity || 1);
}

export function getPackageAggregates(pkg: PackageItem): {
  weightKg: number;
  unitValueBRL: number;
  quantity: number;
} {
  const items = pkg.items || [];
  let weightKg = 0;
  let unitValueBRL = 0;
  let quantity = 0;
  items.forEach((item) => {
    weightKg += itemWeightToKg(item);
    unitValueBRL += (item.unitValueBRL || 0) * (item.quantity || 1);
    quantity += item.quantity || 1;
  });
  return { weightKg, unitValueBRL, quantity: quantity || 1 };
}

export function formatBRL(value: number): string {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
}

export function isPackageFilled(pkg: PackageItem): boolean {
  const { weightKg, unitValueBRL } = getPackageAggregates(pkg);
  return (
    (pkg.widthCm || 0) > 0 ||
    (pkg.heightCm || 0) > 0 ||
    (pkg.lengthCm || 0) > 0 ||
    weightKg > 0 ||
    unitValueBRL > 0 ||
    (pkg.items?.length || 0) > 1
  );
}

export type RouteLocation = {
  id: number;
  name: string;
  state: string;
  slug: string;
  ibge_id?: number;
  /** Full dropoff point name ("CURITIBA GARAGEM"), used only for display. */
  point_name?: string;
};

export type RouteDropoffPoint = {
  id: number;
  name: string;
  address: string | null;
  number: string | null;
  complement: string | null;
  district: string | null;
  cep: string | null;
  phone: string | null;
  available_hours: string | null;
  ibge_id: number;
  city_name: string;
  state_uf: string;
};

export function formatSlugToLabel(slug: string | undefined): string {
  if (!slug) return "";
  const trimmed = slug.trim();
  if (!trimmed) return "";
  const lastDashIndex = trimmed.lastIndexOf("-");
  if (lastDashIndex === -1) {
    return trimmed.replace(/-/g, " ").toUpperCase();
  }
  const citySlug = trimmed.slice(0, lastDashIndex);
  const stateSlug = trimmed.slice(lastDashIndex + 1);
  const city = citySlug.replace(/-/g, " ");
  return `${city} - ${stateSlug}`.toUpperCase();
}
